"use client";

type AuthSessionShape = {
  user?: {
    id?: string | null;
  } | null;
  currentSession?: AuthSessionShape | null;
  session?: AuthSessionShape | null;
  data?: AuthSessionShape | null;
};

const ANON_SCOPE = "anon";
const DEFAULT_PROJECT_REF = "local";
const DEFAULT_APP_HOST = "app";
const REMEMBERED_SCOPE_PREFIX = "syncStorageScopeUser";

function getSupabaseProjectRef(): string {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!url) return DEFAULT_PROJECT_REF;

  try {
    return new URL(url).hostname.split(".")[0] || DEFAULT_PROJECT_REF;
  } catch {
    return DEFAULT_PROJECT_REF;
  }
}

function getSupabaseAuthStorageKey(): string {
  return `sb-${getSupabaseProjectRef()}-auth-token`;
}

function getAppHost(): string {
  if (typeof window !== "undefined") {
    try {
      return new URL(window.location.href).host || DEFAULT_APP_HOST;
    } catch {
      return DEFAULT_APP_HOST;
    }
  }

  const appUrl = process.env.NEXT_PUBLIC_APP_URL;
  if (!appUrl) return DEFAULT_APP_HOST;

  try {
    return new URL(appUrl).host || DEFAULT_APP_HOST;
  } catch {
    return DEFAULT_APP_HOST;
  }
}

function extractUserIdFromSession(value: unknown): string | null {
  if (!value) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractUserIdFromSession(item);
      if (nested) return nested;
    }
    return null;
  }

  if (typeof value !== "object") return null;

  const row = value as AuthSessionShape;
  const directUserId = row.user?.id;
  if (typeof directUserId === "string" && directUserId.length > 0) {
    return directUserId;
  }

  return (
    extractUserIdFromSession(row.currentSession) ??
    extractUserIdFromSession(row.session) ??
    extractUserIdFromSession(row.data)
  );
}

function buildScopedStorageKey(
  baseKey: string,
  scope: { projectRef: string; appHost: string; userId: string | null }
): string {
  const userKey = scope.userId && scope.userId.length > 0 ? scope.userId : ANON_SCOPE;
  return `${baseKey}:${scope.projectRef}:${scope.appHost}:${userKey}`;
}

export function getScopedStorageKeyForUser(baseKey: string, userId: string | null): string {
  return buildScopedStorageKey(baseKey, {
    projectRef: getSupabaseProjectRef(),
    appHost: getAppHost(),
    userId,
  });
}

export function clearRememberedStorageScope(targetStorage?: Storage): void {
  const storageRef =
    targetStorage ?? (typeof window !== "undefined" ? window.localStorage : null);
  if (!storageRef) return;

  const keysToRemove: string[] = [];
  for (let index = 0; index < storageRef.length; index += 1) {
    const key = storageRef.key(index);
    if (key?.startsWith(`${REMEMBERED_SCOPE_PREFIX}:`)) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storageRef.removeItem(key);
  }
}

export function getCurrentStorageScope(): {
  projectRef: string;
  appHost: string;
  userId: string | null;
} {
  const projectRef = getSupabaseProjectRef();
  const appHost = getAppHost();

  if (typeof window === "undefined") {
    return {
      projectRef,
      appHost,
      userId: null,
    };
  }

  const raw = window.localStorage.getItem(getSupabaseAuthStorageKey());
  try {
    const authUserId = raw ? extractUserIdFromSession(JSON.parse(raw)) : null;
    if (authUserId) {
      return {
        projectRef,
        appHost,
        userId: authUserId,
      };
    }
  } catch {
    // Fall through to the anonymous scope when auth storage is malformed.
  }

  return {
    projectRef,
    appHost,
    userId: null,
  };
}

export function getScopedStorageKeyCandidates(baseKey: string): string[] {
  const scope = getCurrentStorageScope();
  const primaryKey = buildScopedStorageKey(baseKey, scope);
  if (!scope.userId) {
    return [primaryKey];
  }

  const anonKey = buildScopedStorageKey(baseKey, {
    projectRef: scope.projectRef,
    appHost: scope.appHost,
    userId: null,
  });

  return anonKey === primaryKey ? [primaryKey] : [primaryKey, anonKey];
}

export function getScopedStorageKey(baseKey: string): string {
  return getScopedStorageKeyCandidates(baseKey)[0];
}
